import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';

type AssessmentsPayload = {
  assessments?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
};

type AssessmentFilterInlineProps = {
  placeholder?: string;
  fetchOptionsApi?: string | any; // expects { assessments: [{id,name}] }
  initialValue?: any; // expects [{ id, evaluation }]
  onApply: (value: string | null) => void;
  queryKey: string;
};

const infoStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#6B7280',
  lineHeight: 1.4,
  marginBottom: 10,
  padding: '6px 10px',
  background: '#F9FAFB',
  borderRadius: 6,
};

const sectionStyle: React.CSSProperties = { marginBottom: 10 };
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  marginBottom: 4,
  color: '#374151',
};
const buttonStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
};
const applyButton: React.CSSProperties = {
  ...buttonStyle,
  background: '#111827',
  color: 'white',
  marginLeft: 8,
};
const clearButton: React.CSSProperties = {
  ...buttonStyle,
  background: 'transparent',
  border: '1px solid #D1D5DB',
  color: '#374151',
};

const AssessmentFilterInline: React.FC<AssessmentFilterInlineProps> = ({
  placeholder,
  fetchOptionsApi,
  initialValue,
  onApply,
  queryKey,
}) => {
  const payload: AssessmentsPayload | null = useMemo(() => {
    try {
      if (!fetchOptionsApi) return null;
      if (typeof fetchOptionsApi === 'string') {
        const maybe = JSON.parse(fetchOptionsApi);
        if (Array.isArray(maybe)) return null;
        return maybe as AssessmentsPayload;
      }
      return fetchOptionsApi as AssessmentsPayload;
    } catch {
      return null;
    }
  }, [fetchOptionsApi]);

  // Parse initial
  const parseInitial = (
    val: any
  ): { assessmentId: string | null; evaluation: boolean | null } => {
    try {
      const arr = typeof val === 'string' ? JSON.parse(val) : val;
      if (Array.isArray(arr) && arr.length > 0) {
        const first = arr[0];
        return { assessmentId: String(first.id), evaluation: first.evaluation };
      }
    } catch {}
    return { assessmentId: null, evaluation: null };
  };

  const init = useMemo(() => parseInitial(initialValue), [initialValue]);
  const [assessmentId, setAssessmentId] = useState<string | null>(
    init.assessmentId
  );
  const [evaluation, setEvaluation] = useState<boolean | null>(init.evaluation);

  useEffect(() => {
    const p = parseInitial(initialValue);
    setAssessmentId(p.assessmentId);
    setEvaluation(p.evaluation);
  }, [initialValue]);

  const assessmentOptions = useMemo(() => {
    const list = payload?.assessments || [];
    return list.map(a => ({ value: a.id, label: a.name }));
  }, [payload]);

  const valueOptions = [
    { value: true, label: 'true' },
    { value: false, label: 'false' },
    { value: null, label: 'not evaluated' },
  ];

  const apply = () => {
    if (!assessmentId) {
      onApply(null);
      return;
    }
    const payload = [{ id: assessmentId, evaluation }];
    onApply(JSON.stringify(payload));
  };

  const clear = () => {
    setAssessmentId(null);
    setEvaluation(null);
    onApply(null);
  };

  return (
    <div
      style={{ padding: '10px 0', width: 280 }}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      <div style={infoStyle}>
        Select an assessment and a value, then click Apply. This filter uses a
        single assessment at a time.
      </div>

      <div style={sectionStyle}>
        <div style={labelStyle}>Assessment</div>
        <Select
          placeholder={placeholder || 'Select assessment...'}
          options={assessmentOptions}
          value={assessmentOptions.find(o => o.value === assessmentId) || null}
          onChange={(opt: any) =>
            setAssessmentId(opt ? String(opt.value) : null)
          }
          isClearable
        />
      </div>

      <div style={sectionStyle}>
        <div style={labelStyle}>Value</div>
        <Select
          placeholder="Select value..."
          options={valueOptions}
          value={valueOptions.find(o => o.value === evaluation) || null}
          onChange={(opt: any) => setEvaluation(opt ? opt.value : null)}
          isClearable
          styles={{
            control: base => ({ ...base, minHeight: 32 }),
            valueContainer: base => ({ ...base, padding: '0 8px' }),
            input: base => ({ ...base, margin: 0 }),
            indicatorsContainer: base => ({ ...base, height: 32 }),
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 12,
        }}
      >
        <button onClick={clear} style={clearButton}>
          Clear
        </button>
        <button onClick={apply} style={applyButton}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default AssessmentFilterInline;
